import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import HelpIcon from '../Documentation/HelpIcon';
import ConflictIndicator from '../ConflictIndicator';
import PublishDraftModal, { type PublishOptions } from '../PublishDraftModal';
import ShareScheduleModal from '../ShareScheduleModal.jsx';
import { conflictManager } from '../../services/ConflictManager';
import copyArrowIcon from '../../assets/copyArrowIcon.png';

const FAB_BUTTON_CLASS = 'relative rounded-full w-10 h-10 flex items-center justify-center shadow-sm transition-all duration-200 ease-in-out';
const FAB_ICON_CLASS = 'w-4 h-4';
const FAB_LABEL_CLASS = 'absolute bottom-full left-1/2 z-[60] mb-2 -translate-x-1/2 px-2.5 py-1 text-[11px] rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none font-medium tracking-wide';

const ShareScheduleModalComponent = ShareScheduleModal as React.ComponentType<any>;

// Blur dominates perceived opacity — keep blur low so background alpha stays meaningful.
const CONTROL_PANEL_GLASS = {
    background: 'rgba(255, 255, 255, 0.38)',
    blur: '6px',
} as const;

const ControlPanelDivider = () => (
    <div className="mx-4 h-6 w-px shrink-0 self-center bg-gradient-to-b from-transparent via-slate-200 to-transparent" aria-hidden="true" />
);

interface ControlPanelSectionProps {
    children: React.ReactNode;
}

const ControlPanelSection: React.FC<ControlPanelSectionProps> = ({ children }) => (
    <div className="flex items-center gap-3 px-1.5">
        {children}
    </div>
);

const OverlapEyeIcon = ({ visible }: { visible: boolean }) => (
    <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className={FAB_ICON_CLASS}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {visible ? (
            <>
                <path d="M2.5 12s3.5-5.5 9.5-5.5 9.5 5.5 9.5 5.5-3.5 5.5-9.5 5.5S2.5 12 2.5 12Z" />
                <circle cx="12" cy="12" r="2.5" />
            </>
        ) : (
            <>
                <path d="M4 10c2 2.5 4.5 3.5 8 3.5s6-1 8-3.5" />
                <path d="M7.5 13.5l-1.2 1.8" />
                <path d="M12 14.5v2.5" />
                <path d="M16.5 13.5l1.2 1.8" />
            </>
        )}
    </svg>
);

interface FloatingActionButtonShellProps {
    hoverLift?: boolean;
    documentationId?: string;
    label?: string;
    labelClassName?: string;
    showLabel?: boolean;
    children: React.ReactNode;
}

const FloatingActionButtonShell: React.FC<FloatingActionButtonShellProps> = ({
    hoverLift = true,
    documentationId,
    label,
    labelClassName = 'bg-slate-800 text-white',
    showLabel = true,
    children,
}) => (
    <div className="relative group overflow-visible">
        <div className="relative overflow-visible">
            <div className={hoverLift ? 'transition-transform duration-200 group-hover:scale-105' : ''}>
                {children}
            </div>
            {documentationId && (
                <HelpIcon
                    documentationId={documentationId}
                    variant="help"
                    appearance="badge"
                    badgeCorner="top-left"
                />
            )}
            {showLabel && label && (
                <span className={`${FAB_LABEL_CLASS} ${labelClassName}`}>
                    {label}
                </span>
            )}
        </div>
    </div>
);

// Helper to detect placeholder entries that should not count towards net changes
const isPlaceholderEntry = (entry: any): boolean => {
    if (!entry) return false;
    const data = typeof entry.toObject === 'function' ? entry.toObject() : entry;
    const name = (data?.displayName || data?.course || data?.title || '').toString().trim();
    if (name === '__PLACEHOLDER__') return true;
    if (data?.type === 'placeholder') return true;
    if (data?.additionalProps?.isPlaceholder === true) return true;
    return false;
};

interface FloatingActionButtonsProps {
    // Button visibility controls
    showSaveDraft: boolean;
    showMakeLive?: boolean;
    showPublishDraft: boolean;
    showConflictIndicator: boolean;
    showOverlappingPeriods?: boolean;
    showOverlappingPeriodEntries?: boolean;
    onShowOverlappingPeriodEntriesChange?: (show: boolean) => void;
    overlappingPeriodCount?: number;
    showCopyViewButton?: boolean;
    showCopyView?: boolean;
    onShowCopyViewChange?: (show: boolean) => void;

    // Button states and data
    isLoading: boolean;
    currentDraftName: string | null;
    currentDraftId?: string | number | null;
    currentDraftPeriodId?: string | number | null;
    currentConflicts: Array<{
        itemId: string;
        itemName: string;
        day: string;
        time: string;
        room: string;
        reasons: Array<{
            reason: string;
            severity: string;
        }>;
    }>;
    // Add props for change tracking
    scheduledInstances: any[];
    serverBaseline: any[];

    // New: change tracking via ID sets
    changeIdCounts?: { created: number; modified: number; deleted: number };
    hasPendingChangesByIdSets?: boolean;

    // Undo/Redo functionality
    canUndo?: boolean;
    canRedo?: boolean;
    onUndo?: () => boolean;
    onRedo?: () => boolean;

    // Event handlers
    onSaveCurrent: () => void;
    onMakeLive?: () => void | Promise<void>;
    onStopLive?: () => void | Promise<void>;
    isDraftActiveForPeriod?: boolean;
    onPublishDraft: (options: PublishOptions) => void | Promise<void>;
    onToggleConflictList: () => void;

    // Data for PublishDraftModal / ShareScheduleModal
    timePeriods?: any[];
    selectedYearForView?: number;
    institutionUrlEndpoint?: string;
    yearStructure?: any;
    shareWeekRange?: {
        startWeek: number;
        startWeekYear: number;
        endWeek: number;
        endWeekYear: number;
    } | null;
    showShareSchedule?: boolean;
    showEditNewVersion?: boolean;
    onEditNewVersion?: () => void;
}

const FloatingActionButtons: React.FC<FloatingActionButtonsProps> = ({
    // Button visibility controls
    showSaveDraft,
    showMakeLive = false,
    showPublishDraft,
    showConflictIndicator,
    showOverlappingPeriods = false,
    showOverlappingPeriodEntries = false,
    onShowOverlappingPeriodEntriesChange,
    overlappingPeriodCount = 0,
    showCopyViewButton = false,
    showCopyView = false,
    onShowCopyViewChange,

    // Button states and data
    isLoading,
    currentDraftName,
    currentDraftId = null,
    currentDraftPeriodId = null,
    currentConflicts,
    scheduledInstances,
    serverBaseline,
    changeIdCounts,
    hasPendingChangesByIdSets,

    // Undo/Redo functionality
    canUndo = false,
    canRedo = false,
    onUndo,
    onRedo,

    // Event handlers
    onSaveCurrent,
    onMakeLive,
    onStopLive,
    isDraftActiveForPeriod = false,
    onPublishDraft,
    onToggleConflictList,

    // Data for PublishDraftModal / ShareScheduleModal
    timePeriods = [],
    selectedYearForView = new Date().getFullYear(),
    institutionUrlEndpoint,
    yearStructure = null,
    shareWeekRange = null,
    showShareSchedule = true,
    showEditNewVersion = false,
    onEditNewVersion
}) => {
    // State for bounce animation
    const [shouldBounce, setShouldBounce] = useState(false);
    const changeIdCountTotal = (changeIdCounts?.created || 0) + (changeIdCounts?.modified || 0) + (changeIdCounts?.deleted || 0);
    const [previousIdCountTotal, setPreviousIdCountTotal] = useState(changeIdCountTotal);
    const [hasNetChanges, setHasNetChanges] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const closeShareModal = useCallback(() => {
        setIsShareModalOpen(false);
    }, []);

    // Function to calculate if there are net changes (similar to useChangeTracking)
    const calculateHasNetChanges = useCallback(() => {
        const baselineReady = Array.isArray(serverBaseline) && serverBaseline.length > 0;
        const instancesReady = Array.isArray(scheduledInstances);
        if (!baselineReady || !instancesReady) {
            console.debug('[FloatingActionButtons] NetChanges early-exit: baseline or instances not ready', {
                baselineReady,
                instancesReady,
                baselineLen: Array.isArray(serverBaseline) ? serverBaseline.length : 'n/a',
                scheduledLen: Array.isArray(scheduledInstances) ? scheduledInstances.length : 'n/a',
                serverBaselineType: typeof serverBaseline,
                scheduledInstancesType: typeof scheduledInstances,
                serverBaselineSample: Array.isArray(serverBaseline) && serverBaseline.length > 0 ? {
                    id: serverBaseline[0].id,
                    course: serverBaseline[0].course
                } : 'no baseline items'
            });
            return false;
        }

        // Ignore placeholder entries when comparing baseline vs current
        const filteredBaseline = (serverBaseline || []).filter(entry => !isPlaceholderEntry(entry));
        const filteredCurrent = (scheduledInstances || []).filter(entry => !isPlaceholderEntry(entry));

        const baselineItemsMap = new Map(filteredBaseline.map(entry => [entry.id, entry]));
        const currentItemsMap = new Map(filteredCurrent.map(entry => [entry.id, entry]));

        // Check for deleted items (in baseline but not in current)
        for (const baselineEntry of filteredBaseline) {
            if (!currentItemsMap.has(baselineEntry.id)) {
                console.debug('[FloatingActionButtons] NetChanges detected: deleted item', {
                    id: baselineEntry.id,
                    name: baselineEntry.displayName || baselineEntry.course || baselineEntry.title
                });
                return true; // Item was deleted
            }
        }

        // Check for created items (in current but not in baseline)
        for (const currentEntry of filteredCurrent) {
            if (!baselineItemsMap.has(currentEntry.id)) {
                console.debug('[FloatingActionButtons] NetChanges detected: created item', {
                    id: currentEntry.id,
                    name: currentEntry.displayName || currentEntry.course || currentEntry.title
                });
                return true; // Item was created
            }
        }

        // Check for modified items
        for (const baselineEntry of filteredBaseline) {
            const currentEntry = currentItemsMap.get(baselineEntry.id);
            if (currentEntry) {
                // Convert current entry to plain object for comparison
                const currentEntryData = currentEntry && typeof currentEntry.toObject === 'function'
                    ? currentEntry.toObject()
                    : currentEntry;

                // Compare key fields
                const fieldsToCompare = [
                    'day', 'timeStart', 'timeEnd', 'course', 'teacher', 'room', 'type', 'weekSeries',
                    // Include description so description-only edits are detected in fallback mode
                    'description'
                ];

                for (const field of fieldsToCompare) {
                    const baselineValue = baselineEntry[field];
                    const currentValue = currentEntryData[field];

                    // Handle special cases for objects
                    if (field === 'teacher' || field === 'room') {
                        const normalizeValue = (val: any) => {
                            if (!val) return 'N/A';
                            if (typeof val === 'string') return val;
                            if (typeof val === 'object') return val.name || val.room_code || 'N/A';
                            return String(val);
                        };
                        if (normalizeValue(baselineValue) !== normalizeValue(currentValue)) {
                            console.debug('[FloatingActionButtons] NetChanges detected: modified field', {
                                id: baselineEntry.id,
                                field,
                                baseline: normalizeValue(baselineValue),
                                current: normalizeValue(currentValue)
                            });
                            return true; // Field was modified
                        }
                    } else if (field === 'groups') {
                        const normalizeGroups = (val: any) => {
                            if (!Array.isArray(val)) return 'N/A';
                            return val.map((g: any) => {
                                if (typeof g === 'object' && g !== null) return g.name || g.group_code || g.id;
                                return g;
                            }).join(', ');
                        };
                        if (normalizeGroups(baselineValue) !== normalizeGroups(currentValue)) {
                            console.debug('[FloatingActionButtons] NetChanges detected: modified groups', {
                                id: baselineEntry.id,
                                baseline: normalizeGroups(baselineValue),
                                current: normalizeGroups(currentValue)
                            });
                            return true; // Groups were modified
                        }
                    } else if (baselineValue !== currentValue) {
                        console.debug('[FloatingActionButtons] NetChanges detected: modified primitive field', {
                            id: baselineEntry.id,
                            field,
                            baseline: baselineValue,
                            current: currentValue
                        });
                        return true; // Field was modified
                    }
                }

                // Compare exceptions arrays
                const baselineExceptions = Array.isArray(baselineEntry.exceptions) ? baselineEntry.exceptions : [];
                const currentExceptions = Array.isArray(currentEntryData.exceptions) ? currentEntryData.exceptions : [];

                if (baselineExceptions.length !== currentExceptions.length) {
                    console.debug('[FloatingActionButtons] NetChanges detected: exceptions length change', {
                        id: baselineEntry.id,
                        baselineLen: baselineExceptions.length,
                        currentLen: currentExceptions.length
                    });
                    return true; // Exceptions were added or removed
                }

                // Check if any exceptions were modified
                const baselineExceptionsMap = new Map(
                    baselineExceptions.map((ex: any) => [`${ex.week}-${ex.year}`, ex])
                );
                const currentExceptionsMap = new Map(
                    currentExceptions.map((ex: any) => [`${ex.week}-${ex.year}`, ex])
                );

                // Check each baseline exception against current exceptions
                for (let i = 0; i < baselineExceptions.length; i++) {
                    const baselineException = baselineExceptions[i];
                    const key = `${baselineException.week}-${baselineException.year}`;
                    const currentException = currentExceptionsMap.get(key);
                    if (!currentException || JSON.stringify(baselineException) !== JSON.stringify(currentException)) {
                        console.debug('[FloatingActionButtons] NetChanges detected: exception modified', {
                            id: baselineEntry.id,
                            week: baselineException.week,
                            year: baselineException.year
                        });
                        return true; // Exception was modified
                    }
                }
            }
        }

        return false; // No net changes found
    }, [serverBaseline, scheduledInstances]);



    // Track ID-set change count to trigger bounce animation
    useEffect(() => {
        if (changeIdCountTotal !== previousIdCountTotal) {
            setPreviousIdCountTotal(changeIdCountTotal);
            if (changeIdCountTotal > 0) {
                setShouldBounce(true);
                const timer = setTimeout(() => setShouldBounce(false), 600);
                return () => clearTimeout(timer);
            }
        }
        return undefined;
    }, [changeIdCountTotal, previousIdCountTotal]);

    // Calculate net changes when scheduledInstances or serverBaseline change
    useEffect(() => {
        const netChanges = calculateHasNetChanges();
        setHasNetChanges(netChanges);
        console.debug('[FloatingActionButtons] NetChanges recalculated', {
            baselineLen: Array.isArray(serverBaseline) ? serverBaseline.length : 'n/a',
            scheduledLen: Array.isArray(scheduledInstances) ? scheduledInstances.length : 'n/a',
            hasNetChanges: netChanges
        });
    }, [calculateHasNetChanges]);

    // Determine if save draft button should be disabled
    // Use net changes for draft mode
    const baselineReady = Array.isArray(serverBaseline) && serverBaseline.length > 0;

    // Prefer ID-set based pending changes signal when provided, fallback to legacy net-changes
    const idSetHasChanges = typeof hasPendingChangesByIdSets === 'boolean' ? hasPendingChangesByIdSets : false;
    const effectiveHasChanges = idSetHasChanges || hasNetChanges;

    // Log why the Save Draft button is enabled/disabled on every relevant change
    useEffect(() => {
        console.debug('[FloatingActionButtons] Save Draft button state', {
            showSaveDraft,
            isLoading,
            baselineReady,
            hasNetChanges,
        });
    }, [showSaveDraft, isLoading, baselineReady, hasNetChanges, idSetHasChanges]);
    const hasUndoRedo = canUndo || canRedo;

    const hasDraftActions = showSaveDraft || showMakeLive;
    const hasShareActions = showEditNewVersion || showShareSchedule;
    const hasViewTools = showCopyViewButton || showOverlappingPeriods || showConflictIndicator;

    // Track dismissed changes so badge updates when conflicts are dismissed elsewhere
    const [dismissedVersion, setDismissedVersion] = useState(0);
    useEffect(() => {
        const unsubscribe = conflictManager.onDismissedChange(() => setDismissedVersion(v => v + 1));
        return () => unsubscribe();
    }, []);

    // Count only conflicts that still have at least one non-dismissed reason
    const activeConflictCount = useMemo(() => {
        if (!Array.isArray(currentConflicts)) return 0;
        return currentConflicts.reduce((sum, conflict) => {
            const hasActiveReason = (conflict.reasons || []).some(reason => {
                const baseMessage = reason.reason.replace(/, week \d+/i, '').replace(/\(week \d+\)/i, '').trim();
                const reasonId = conflictManager.makeConflictId(conflict.itemId, baseMessage);
                return !conflictManager.isConflictDismissed(reasonId);
            });
            return hasActiveReason ? sum + 1 : sum;
        }, 0);
    }, [currentConflicts, dismissedVersion]);

    const undoRedoButtonClass = (enabled: boolean) => `${FAB_BUTTON_CLASS} ${enabled
        ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 ring-1 ring-slate-200/80'
        : 'bg-slate-50 text-slate-300 cursor-not-allowed ring-1 ring-slate-100'
        }`;

    return (
        <>
            {createPortal(
                <div
                    className="fixed bottom-0 left-1/2 z-50 w-max max-w-4xl -translate-x-1/2 overflow-visible"
                    role="toolbar"
                    aria-label="Schedule controls"
                >
                    <div className="relative overflow-visible rounded-t-xl">
                        <div
                            className="pointer-events-none absolute inset-0 rounded-t-xl border border-b-0 border-white/35 shadow-[0_-4px_24px_rgba(15,23,42,0.08),0_-1px_3px_rgba(15,23,42,0.04)]"
                            style={{
                                backgroundColor: CONTROL_PANEL_GLASS.background,
                                WebkitBackdropFilter: `blur(${CONTROL_PANEL_GLASS.blur})`,
                                backdropFilter: `blur(${CONTROL_PANEL_GLASS.blur})`,
                            }}
                            aria-hidden="true"
                        />
                        {/* Top accent — subtle instrument-panel highlight */}
                        <div
                            className="pointer-events-none absolute inset-x-4 top-0 z-10 h-px bg-gradient-to-r from-transparent via-slate-400/50 to-transparent"
                            aria-hidden="true"
                        />

                        <div className="relative z-10 flex items-center justify-center gap-1.5 overflow-visible px-4 py-2">
                            {hasUndoRedo && (
                                <>
                                    <ControlPanelSection>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (canUndo && onUndo) {
                                                    onUndo();
                                                }
                                            }}
                                            disabled={!canUndo}
                                            className={undoRedoButtonClass(canUndo)}
                                            aria-label="Undo"
                                            title="Undo"
                                        >
                                            <svg className={FAB_ICON_CLASS} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (canRedo && onRedo) {
                                                    onRedo();
                                                }
                                            }}
                                            disabled={!canRedo}
                                            className={undoRedoButtonClass(canRedo)}
                                            aria-label="Redo"
                                            title="Redo"
                                        >
                                            <svg className={FAB_ICON_CLASS} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6-6m6 6l-6 6" />
                                            </svg>
                                        </button>
                                    </ControlPanelSection>
                                    {(hasDraftActions || hasShareActions || hasViewTools) && <ControlPanelDivider />}
                                </>
                            )}

                            {hasDraftActions && (
                                <>
                                    <ControlPanelSection>
                                        {showSaveDraft && (
                                            <FloatingActionButtonShell
                                                hoverLift={changeIdCountTotal > 0}
                                                documentationId="save-draft-button"
                                                label="Save Draft"
                                                labelClassName="bg-blue-600 text-white"
                                                showLabel={changeIdCountTotal > 0}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={onSaveCurrent}
                                                    disabled={isLoading || changeIdCountTotal === 0}
                                                    className={`${FAB_BUTTON_CLASS} ${shouldBounce ? 'animate-bounce-once' : ''} ${isLoading || changeIdCountTotal === 0
                                                        ? 'bg-slate-100 text-slate-400 ring-1 ring-slate-200/80 pointer-events-none'
                                                        : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                                                        }`}
                                                >
                                                    <svg className={FAB_ICON_CLASS} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                                                        <polyline strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} points="17,21 17,13 7,13 7,21" />
                                                        <polyline strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} points="7,3 7,8 15,8" />
                                                    </svg>
                                                    {changeIdCountTotal > 0 && (
                                                        <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-0.5 text-[9px] font-bold leading-none text-white pointer-events-none">
                                                            {changeIdCountTotal}
                                                        </span>
                                                    )}
                                                </button>
                                            </FloatingActionButtonShell>
                                        )}

                                        {showMakeLive && (
                                            <FloatingActionButtonShell
                                                hoverLift={!isLoading}
                                                documentationId={isDraftActiveForPeriod ? 'stop-draft-live-button' : 'make-draft-live-button'}
                                                label={isDraftActiveForPeriod ? 'Remove From Live' : 'Make Live'}
                                                labelClassName={isDraftActiveForPeriod ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}
                                                showLabel={!isLoading}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (isDraftActiveForPeriod) {
                                                            onStopLive && onStopLive();
                                                        } else {
                                                            onMakeLive && onMakeLive();
                                                        }
                                                    }}
                                                    disabled={isLoading}
                                                    className={`${FAB_BUTTON_CLASS} disabled:opacity-50 disabled:cursor-not-allowed ${isDraftActiveForPeriod
                                                        ? 'bg-red-600 hover:bg-red-700 text-white'
                                                        : 'bg-green-600 hover:bg-green-700 text-white'
                                                        }`}
                                                    title={isDraftActiveForPeriod
                                                        ? 'Stop this draft — entries will no longer be visible to students and teachers'
                                                        : 'Make this draft the live schedule for its period'}
                                                    aria-label={isDraftActiveForPeriod ? 'Stop Live' : 'Make Live'}
                                                >
                                                    {isDraftActiveForPeriod ? (
                                                        <svg className={FAB_ICON_CLASS} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                            <rect x="6" y="6" width="12" height="12" rx="1" />
                                                        </svg>
                                                    ) : (
                                                        <svg className={FAB_ICON_CLASS} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                            <path d="M8 5.5v13a1 1 0 001.52.85l10-6.5a1 1 0 000-1.7l-10-6.5A1 1 0 008 5.5z" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </FloatingActionButtonShell>
                                        )}
                                    </ControlPanelSection>
                                    {(hasShareActions || hasViewTools) && <ControlPanelDivider />}
                                </>
                            )}

                            {hasShareActions && (
                                <>
                                    <ControlPanelSection>
                                        {showEditNewVersion ? (
                                            <FloatingActionButtonShell
                                                hoverLift={!isLoading}
                                                documentationId="edit-new-version-button"
                                                label="Edit"
                                                labelClassName="bg-green-700 text-white"
                                                showLabel={!isLoading}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => onEditNewVersion && onEditNewVersion()}
                                                    disabled={isLoading}
                                                    className={`${FAB_BUTTON_CLASS} bg-green-700 hover:bg-green-800 text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    aria-label="Edit"
                                                >
                                                    <svg className={FAB_ICON_CLASS} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                            </FloatingActionButtonShell>
                                        ) : (
                                            <FloatingActionButtonShell
                                                hoverLift={!isLoading}
                                                documentationId="share-schedule-button"
                                                label="Share Schedule"
                                                labelClassName="bg-purple-600 text-white"
                                                showLabel={!isLoading}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => setIsShareModalOpen(true)}
                                                    disabled={isLoading}
                                                    className={`${FAB_BUTTON_CLASS} bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    <svg className={FAB_ICON_CLASS} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 11-5.656-5.656l1.172-1.172M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 115.656 5.656L17.656 10" />
                                                    </svg>
                                                </button>
                                            </FloatingActionButtonShell>
                                        )}
                                    </ControlPanelSection>
                                    {hasViewTools && <ControlPanelDivider />}
                                </>
                            )}

                            {hasViewTools && (
                                <ControlPanelSection>
                                    {showCopyViewButton && typeof onShowCopyViewChange === 'function' && (
                                        <FloatingActionButtonShell
                                            documentationId="compare-drafts-button"
                                            label={showCopyView ? 'Close Compare View' : 'Compare & Merge Versions'}
                                            labelClassName={showCopyView ? 'bg-indigo-600 text-white' : 'bg-orange-600 text-white'}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => onShowCopyViewChange(!showCopyView)}
                                                className={`${FAB_BUTTON_CLASS} ${showCopyView
                                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                                                    }`}
                                                aria-pressed={showCopyView}
                                                aria-label={showCopyView ? 'Exit copy view' : 'Open copy view to compare drafts'}
                                                title={showCopyView ? 'Exit copy view' : 'Compare and copy entries between versions, including other periods'}
                                            >
                                                <img src={copyArrowIcon} alt="" aria-hidden="true" className={FAB_ICON_CLASS} />
                                            </button>
                                        </FloatingActionButtonShell>
                                    )}

                                    {showOverlappingPeriods && typeof onShowOverlappingPeriodEntriesChange === 'function' && (
                                        <FloatingActionButtonShell
                                            documentationId="show-overlapping-button"
                                            label={showOverlappingPeriodEntries ? 'Show Period Alone' : 'Show with other Periods'}
                                            labelClassName={showOverlappingPeriodEntries ? 'bg-blue-600 text-white' : 'bg-slate-700 text-white'}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => onShowOverlappingPeriodEntriesChange(!showOverlappingPeriodEntries)}
                                                className={`${FAB_BUTTON_CLASS} ${showOverlappingPeriodEntries
                                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                                    : 'bg-white text-slate-700 hover:bg-slate-50 ring-1 ring-slate-200/80'
                                                    }`}
                                                aria-pressed={showOverlappingPeriodEntries}
                                                aria-label={showOverlappingPeriodEntries
                                                    ? `Showing entries from ${overlappingPeriodCount} overlapping periods`
                                                    : `Show entries from ${overlappingPeriodCount} overlapping periods`}
                                                title={showOverlappingPeriodEntries
                                                    ? `Showing entries from ${overlappingPeriodCount} overlapping periods`
                                                    : `Show entries from ${overlappingPeriodCount} periods that overlap this range`}
                                            >
                                                <OverlapEyeIcon visible={showOverlappingPeriodEntries} />
                                            </button>
                                        </FloatingActionButtonShell>
                                    )}

                                    {showConflictIndicator && (
                                        <FloatingActionButtonShell
                                            label="See Conflicts"
                                            labelClassName="bg-red-600 text-white"
                                        >
                                            <ConflictIndicator
                                                conflictCount={activeConflictCount}
                                                onClick={onToggleConflictList}
                                            />
                                        </FloatingActionButtonShell>
                                    )}
                                </ControlPanelSection>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Publish Draft Modal */}
            {showPublishDraft && (
            <PublishDraftModal
                isOpen={isPublishModalOpen}
                onClose={() => setIsPublishModalOpen(false)}
                onPublish={onPublishDraft}
                currentDraftName={currentDraftName || ''}
                timePeriods={timePeriods}
                scheduledInstances={scheduledInstances}
                selectedYearForView={selectedYearForView}
            />
            )}

            {/* Share Schedule Modal */}
            <ShareScheduleModalComponent
                isOpen={isShareModalOpen}
                onClose={closeShareModal}
                institutionUrlEndpoint={institutionUrlEndpoint || ''}
                timePeriods={timePeriods}
                yearStructure={yearStructure}
                initialStartWeek={shareWeekRange?.startWeek}
                initialStartWeekYear={shareWeekRange?.startWeekYear}
                initialEndWeek={shareWeekRange?.endWeek}
                initialEndWeekYear={shareWeekRange?.endWeekYear}
                currentDraftId={currentDraftId}
                currentDraftPeriodId={currentDraftPeriodId}
                currentDraftName={currentDraftName}
            />
        </>
    );
};

export default FloatingActionButtons;
